import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// A bundled section from Shopify is rendered immediately. This delay is used
// only by the reconciliation fallback when a response did not include the
// requested cart section.
const CART_SECTION_REFRESH_DELAY = 0;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {number | undefined} */
  #cartSectionRefreshTimer;

  /** @type {boolean} */
  #cartSectionRefreshInFlight = false;

  /** @type {boolean} */
  #cartSectionRefreshPending = false;

  /** @type {boolean} */
  #cartNeedsRefresh = false;

  /** @type {Set<string>} */
  #pendingCartOperations = new Set();

  /** @type {number} */
  #cartMutationGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#cartSectionRefreshTimer) {
      clearTimeout(this.#cartSectionRefreshTimer);
      this.#cartSectionRefreshTimer = undefined;
    }

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        this.#syncDrawerShell(newSectionHTML);
        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const eventData = event.detail.data;
    const source = eventData?.source;

    // Keep the visible cart stable while a burst of add requests is in flight.
    // Rendering an earlier response here can make the cart count briefly step
    // backwards after a faster subsequent click.
    if (source === 'product-form-component' && eventData?.phase === 'pending') {
      this.#cartMutationGeneration += 1;
      this.#cartNeedsRefresh = true;
      if (this.#cartSectionRefreshTimer !== undefined) {
        clearTimeout(this.#cartSectionRefreshTimer);
        this.#cartSectionRefreshTimer = undefined;
        this.#cartSectionRefreshPending = false;
      }
      this.#trackPendingOperations(eventData.operationIds, true);
      if (this.#isCartVisible()) this.#updateSyncState();
      return;
    }

    if (source === 'cart-section-reconciliation' || source === 'cart-api-reconciliation') return;
    if (source === 'product-form-component') {
      this.#trackPendingOperations(eventData.operationIds, false);
      this.#cartNeedsRefresh = true;

      // Shopify already returned the fresh drawer with this mutation. Morph it
      // while the drawer is closed as well, so clicking the cart icon later
      // never starts a second request or exposes the long "Adding…" overlay.

      if (eventData?.didError) {
        // A validation/network error can still leave Shopify with a partially
        // changed cart. Refresh from the server instead of assuming a rollback.
        this.#cartSectionRefreshPending = true;
        this.#updateSyncState();
        if (this.#pendingCartOperations.size === 0) this.#queueCartSectionRefresh();
        return;
      }
    }

    const cartItemsHtml = eventData?.sections?.[this.sectionId];
    if (source === 'product-form-component') {
      if (cartItemsHtml && this.#pendingCartOperations.size === 0) {
        this.#cartSectionRefreshPending = false;
        void this.#renderCartSection(cartItemsHtml);
        return;
      }

      // A later add is still in flight, so this response represents only a
      // partial cart. Keep the loading layer until the final batch returns.
      if (this.#pendingCartOperations.size > 0) {
        this.#updateSyncState();
        return;
      }

      // Fallback for a response without bundled section rendering.
      this.#cartSectionRefreshPending = true;
      this.#updateSyncState();
      this.#queueCartSectionRefresh();
    } else if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * @param {string[] | undefined} operationIds
   * @param {boolean} pending
   */
  #trackPendingOperations(operationIds, pending) {
    for (const operationId of operationIds ?? []) {
      if (pending) {
        this.#pendingCartOperations.add(operationId);
      } else {
        this.#pendingCartOperations.delete(operationId);
      }
    }
  }

  #updateSyncState() {
    const hasPendingCartWork =
      this.#pendingCartOperations.size > 0 || this.#cartSectionRefreshPending || this.#cartSectionRefreshInFlight;
    const isSyncing = this.#isCartVisible() && hasPendingCartWork;
    const syncingIndicator = /** @type {HTMLElement | null} */ (this.querySelector('[data-cart-syncing]'));

    this.toggleAttribute('aria-busy', isSyncing);
    if (syncingIndicator) syncingIndicator.hidden = !isSyncing;

    for (const checkoutButton of this.querySelectorAll('[name="checkout"]')) {
      if (!(checkoutButton instanceof HTMLButtonElement)) continue;

      if (isSyncing && !checkoutButton.disabled) {
        checkoutButton.dataset.cartSyncDisabled = 'true';
        checkoutButton.disabled = true;
      } else if (!isSyncing && checkoutButton.dataset.cartSyncDisabled === 'true') {
        checkoutButton.disabled = false;
        delete checkoutButton.dataset.cartSyncDisabled;
      }
    }

    for (const acceleratedCheckout of this.querySelectorAll('.additional-checkout-buttons')) {
      if (acceleratedCheckout instanceof HTMLElement) acceleratedCheckout.inert = isSyncing;
    }
  }

  /**
   * Queues the one authoritative cart-drawer render after a product burst.
   * @param {number} [delay]
   */
  #queueCartSectionRefresh(delay = CART_SECTION_REFRESH_DELAY) {
    this.#cartNeedsRefresh = true;
    if (!this.#isCartVisible()) return;

    this.#cartSectionRefreshPending = true;
    this.#updateSyncState();

    if (this.#cartSectionRefreshTimer) {
      clearTimeout(this.#cartSectionRefreshTimer);
    }

    this.#cartSectionRefreshTimer = setTimeout(() => {
      this.#cartSectionRefreshTimer = undefined;
      void this.#refreshCartSection();
    }, delay);
  }

  async #refreshCartSection() {
    if (this.#cartSectionRefreshInFlight || !this.#cartSectionRefreshPending) return;

    this.#cartSectionRefreshPending = false;
    this.#cartSectionRefreshInFlight = true;
    this.#updateSyncState();
    const mutationGeneration = this.#cartMutationGeneration;

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, false);

      // An add made while the section was loading means its response is stale.
      // Keep only the final response for the drawer and cart bubble.
      if (mutationGeneration !== this.#cartMutationGeneration || this.#pendingCartOperations.size > 0) {
        this.#cartSectionRefreshPending = true;
        return;
      }

      await this.#renderCartSection(sectionHTML);
    } catch (error) {
      console.error(error);
    } finally {
      this.#cartSectionRefreshInFlight = false;

      if (this.#cartSectionRefreshPending && this.#pendingCartOperations.size === 0) {
        this.#queueCartSectionRefresh();
      }

      this.#updateSyncState();
    }
  }

  /**
   * @param {string} sectionHTML
   * @param {number | undefined} knownCartCount
   */
  async #renderCartSection(sectionHTML, knownCartCount) {
    const renderedSection = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const rawCartCount = renderedSection.querySelector('[ref="cartItemCount"], [ref="cartBubbleCount"]')?.textContent;
    const cartCount = knownCartCount ?? Number.parseInt(rawCartCount ?? '', 10);

    this.#syncDrawerShell(renderedSection);
    await morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });
    this.#cartNeedsRefresh = false;
    this.#updateCartQuantitySelectorButtonStates();

    if (Number.isFinite(cartCount)) {
      this.#syncHeaderCartCount(cartCount);
    }

    this.#updateSyncState();
    document.dispatchEvent(
      new CustomEvent('cart:sections-rendered', {
        detail: {
          source: 'product-form-component',
          isDrawer: this.isDrawer,
          autoOpen: Boolean(document.querySelector('cart-drawer-component[auto-open]')),
        },
      })
    );
  }

  /**
   * Hydration intentionally morphs only cart-drawer__inner. Keep the dialog's
   * stateful shell in sync as well, otherwise an initially empty drawer keeps
   * cart-drawer--empty styles after products have been inserted (and vice
   * versa).
   *
   * @param {Document} renderedSection
   */
  #syncDrawerShell(renderedSection) {
    if (!this.isDrawer) return;

    const currentDialog = this.closest('.cart-drawer__dialog');
    const renderedDialog = renderedSection.querySelector('.cart-drawer__dialog');
    if (!(currentDialog instanceof HTMLDialogElement) || !(renderedDialog instanceof HTMLDialogElement)) return;

    const isEmpty = renderedDialog.classList.contains('cart-drawer--empty');
    currentDialog.classList.toggle('cart-drawer--empty', isEmpty);

    const labelledBy = renderedDialog.getAttribute('aria-labelledby');
    if (labelledBy) currentDialog.setAttribute('aria-labelledby', labelledBy);
  }

  /** @param {number} itemCount */
  #syncHeaderCartCount(itemCount) {
    for (const cartIcon of document.querySelectorAll('cart-icon')) {
      const renderCartBubble = /** @type {any} */ (cartIcon).renderCartBubble;
      if (typeof renderCartBubble === 'function') {
        void renderCartBubble.call(cartIcon, itemCount, false, false);
      }
    }
  }

  /**
   * Called by the cart drawer when a customer opens it. Hidden drawers don't
   * spend time rendering every cart line after each add-to-cart action.
   */
  refreshCartIfNeeded() {
    if (!this.#cartNeedsRefresh) return;

    if (this.#pendingCartOperations.size > 0) {
      this.#cartSectionRefreshPending = true;
      this.#updateSyncState();
      return;
    }

    this.#queueCartSectionRefresh(0);
  }

  #isCartVisible() {
    if (!this.isDrawer) return true;
    return this.closest('dialog')?.open === true;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
